import React, { useState, useEffect } from 'react';
import { onAuthStateChanged, signOut, sendEmailVerification } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from './firebase/config';
import { ThemeProvider } from './contexts/ThemeContext';
import { showToast } from './utils/toast'; // Importando o utilitário de toast
import GlobalStyles from './components/GlobalStyles';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import ProfilePage from './pages/ProfilePage';
import SettingsPage from './pages/SettingsPage';
import PendingApprovalPage from './pages/PendingApprovalPage';
import UsersPage from './pages/UsersPage';
import 'bootstrap/dist/css/bootstrap.min.css';
import { MailWarning } from 'lucide-react';

/**
 * Componente de layout principal que inclui a Sidebar, o Header e o conteúdo da página ativa.
 * Agora também inclui um banner para verificação de e-mail.
 */
function MainLayout({ user, mspData }) {
    const [activePage, setActivePage] = useState('dashboard');

    /**
     * Um banner que aparece no topo da página se o e-mail do usuário não for verificado.
     */
    const EmailVerificationBanner = () => {
        if (user.emailVerified) {
            return null;
        }

        const handleResendEmail = async () => {
            try {
                if (auth.currentUser) {
                    await sendEmailVerification(auth.currentUser);
                    // Substituído Swal.fire por showToast
                    showToast('success', 'Enviamos um novo link de verificação!');
                }
            } catch (error) {
                console.error("Erro ao reenviar e-mail de verificação:", error);
                // Substituído Swal.fire por showToast
                showToast('error', 'Não foi possível reenviar o e-mail.');
            }
        };

        return (
            <div className="alert alert-warning rounded-0 border-0 border-bottom d-flex align-items-center justify-content-center p-2 mb-0 text-center">
                <MailWarning size={18} className="me-2 flex-shrink-0" />
                <span className="me-3">Seu e-mail ainda não foi verificado. Algumas funcionalidades, como adicionar tenants, estão desabilitadas.</span>
                <button onClick={handleResendEmail} className="btn btn-sm btn-primary">Reenviar E-mail</button>
            </div>
        );
    };

    const renderActivePage = () => {
        switch (activePage) {
            case 'dashboard':
                return <DashboardPage user={user} mspData={mspData} />;
            case 'profile':
                return <ProfilePage user={user} mspData={mspData} />;
            case 'settings':
                return <SettingsPage user={user} mspData={mspData} />;
            case 'users':
                return mspData?.isAdmin ? <UsersPage /> : <DashboardPage user={user} mspData={mspData} />;
            default:
                return <DashboardPage user={user} mspData={mspData} />;
        }
    };

    return (
        <div className="d-flex" style={{ minHeight: '100vh' }}>
            <Sidebar activePage={activePage} setActivePage={setActivePage} mspData={mspData} />
            <div className="d-flex flex-column flex-grow-1">
                <Header user={user} mspData={mspData} setActivePage={setActivePage} />
                <EmailVerificationBanner />
                <main className="flex-grow-1 p-4 bg-body-tertiary">
                    {renderActivePage()}
                </main>
            </div>
        </div>
    );
}

/**
 * Componente raiz da aplicação. Gerencia o estado de autenticação e os dados do usuário.
 */
export default function App() {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [mspData, setMspData] = useState(null);
    // Estado "dummy" para forçar a atualização da interface quando necessário.
    const [, setForceUpdate] = useState(0);

    useEffect(() => {
        const unsubscribeAuth = onAuthStateChanged(auth, (currentUser) => {
            setUser(currentUser);
            if (!currentUser) {
                setMspData(null);
                setLoading(false);
            }
        });
        return () => unsubscribeAuth();
    }, []);

    useEffect(() => {
        let unsubscribeDoc;
        if (user) {
            const userDocRef = doc(db, 'msp_users', user.uid);
            unsubscribeDoc = onSnapshot(userDocRef, (doc) => {
                if (doc.exists()) {
                    const data = doc.data();
                    setMspData(data);
                    if (data.status === 'disabled') {
                        // Lógica alterada: removemos o Swal.fire daqui.
                        // A mensagem será exibida na tela de Login.
                        // Armazenamos o motivo no sessionStorage para que a LoginPage possa ler.
                        sessionStorage.setItem('logoutReason', 'disabled');
                        signOut(auth);
                    }
                } else {
                    signOut(auth);
                }
                setLoading(false);
            }, (error) => {
                console.error("Erro ao buscar perfil do Firestore:", error);
                setLoading(false);
            });
        }
        return () => {
            if (unsubscribeDoc) unsubscribeDoc();
        };
    }, [user]);

    // Lógica de verificação de e-mail (sem alterações de modal)
    useEffect(() => {
        if (!user || user.emailVerified) return;

        const intervalId = setInterval(async () => {
            if (auth.currentUser) {
                await auth.currentUser.reload();
                if (auth.currentUser.emailVerified) {
                    clearInterval(intervalId);
                    setForceUpdate(tick => tick + 1);
                }
            }
        }, 3000);

        return () => clearInterval(intervalId);
    }, [user]);

    if (loading) {
        return <div className="d-flex justify-content-center align-items-center vh-100">Carregando aplicação...</div>;
    }

    const renderContent = () => {
        if (!user) {
            return <LoginPage />;
        }
        if (!mspData) {
            return <div className="d-flex justify-content-center align-items-center vh-100">Verificando status da conta...</div>;
        }

        switch (mspData.status) {
            case 'active':
                return <MainLayout user={user} mspData={mspData} />;
            case 'pending_approval':
                return <PendingApprovalPage />;
            case 'disabled':
                // Este caso agora renderiza a LoginPage, que mostrará a mensagem.
                return <LoginPage />;
            default:
                return <div className="d-flex justify-content-center align-items-center vh-100">Status de conta desconhecido.</div>;
        }
    };

    return (
        <ThemeProvider>
            <GlobalStyles
                brandColorLight={mspData?.brandColorLight}
                brandColorDark={mspData?.brandColorDark}
            />
            {renderContent()}
        </ThemeProvider>
    );
}